import type { IconType } from "react-icons";
import type { ComponentType } from "react";
import { FaCoins } from "react-icons/fa6";
import { IoCardOutline } from "react-icons/io5";
import {
  FaShippingFast,
  FaTools,
  FaServicestack,
  FaWarehouse,
  FaBullseye,
} from "react-icons/fa";
import {
  MdOutlineSwitchAccessShortcutAdd,
  MdCleanHands,
  MdOutlineMoneyOff,
} from "react-icons/md";
import { BsCashCoin } from "react-icons/bs";
import { TbCash } from "react-icons/tb";
import { SiSamsungpay } from "react-icons/si";
import { FaArrowUpWideShort } from "react-icons/fa6";
import { IoMdAdd, IoMdRemoveCircleOutline } from "react-icons/io";
import {
  VisaIcon,
  AmexIcon,
  ApplePayIcon,
  GooglePayIcon,
  MastercardIcon,
  DiscoverIcon,
} from "./icons/payment";

export const STATE_OPTIONS = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const fallbackCountries = [
  "United States",
  "Canada",
  "Mexico",
  "United Kingdom",
  "Ireland",
  "France",
  "Germany",
  "Italy",
  "Spain",
  "Portugal",
  "Netherlands",
  "Belgium",
  "Switzerland",
  "Austria",
  "Denmark",
  "Sweden",
  "Norway",
  "Finland",
  "Australia",
  "New Zealand",
  "Japan",
  "South Korea",
  "China",
  "India",
  "Brazil",
  "Argentina",
  "Chile",
  "Colombia",
  "South Africa",
];

export type CountryOption = { code: string; name: string };

const fallbackCountryCodes: Record<string, string> = {
  "United States": "US", Canada: "CA", Mexico: "MX", "United Kingdom": "GB",
  Ireland: "IE", France: "FR", Germany: "DE", Italy: "IT", Spain: "ES",
  Portugal: "PT", Netherlands: "NL", Belgium: "BE", Switzerland: "CH",
  Austria: "AT", Denmark: "DK", Sweden: "SE", Norway: "NO", Finland: "FI",
  Australia: "AU", "New Zealand": "NZ", Japan: "JP", "South Korea": "KR",
  China: "CN", India: "IN", Brazil: "BR", Argentina: "AR", Chile: "CL",
  Colombia: "CO", "South Africa": "ZA",
};

function buildCountryOptions(): CountryOption[] {
  const intlApi = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
    DisplayNames?: new (
      locales: string[],
      options: { type: string },
    ) => { of: (code: string) => string | undefined };
  };
  try {
    const displayNames = intlApi.DisplayNames
      ? new intlApi.DisplayNames(["en"], { type: "region" })
      : null;
    if (displayNames) {
      const options: CountryOption[] = [];
      for (let first = 65; first <= 90; first += 1) {
        for (let second = 65; second <= 90; second += 1) {
          const code = String.fromCharCode(first, second);
          const name = displayNames.of(code);
          if (name && name !== code && !/unknown region/i.test(name)) {
            options.push({ code, name });
          }
        }
      }
      if (options.length) {
        return options.sort((left, right) => left.name.localeCompare(right.name));
      }
    }
  } catch {
    // Fall back to common countries when Intl region data is unavailable.
  }
  return fallbackCountries.map((name) => ({
    name,
    code: fallbackCountryCodes[name] || name,
  }));
}

export const COUNTRY_OPTIONS = buildCountryOptions();
export const COUNTRIES = COUNTRY_OPTIONS.map(({ name }) => name);

export const isUnitedStatesCountry = (country?: string | null) => {
  const normalized = (country || "").trim().toLowerCase();
  return (
    !normalized ||
    normalized === "united states" ||
    normalized === "united states of america" ||
    normalized === "usa" ||
    normalized === "us"
  );
};

export const addressRegionLabel = (country?: string | null) => {
  if (isUnitedStatesCountry(country)) return "State";
  if ((country || "").trim().toLowerCase() === "canada") return "Province";
  return "Region / State";
};

export const postalCodeLabel = (country?: string | null) =>
  isUnitedStatesCountry(country) ? "ZIP" : "Postal Code";

export const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const PAYMENT_FORMS = [
  { key: "cash", label: "Cash", icon: TbCash },
  { key: "coin", label: "Coin", icon: FaCoins },
  { key: "card", label: "Card", icon: IoCardOutline },
];

// Icon mapping for payment types/brands; adjust className to tweak brand colors.
type PaymentIcon = IconType | ComponentType<{ className?: string }>;

export const CARD_PAYMENT_ICONS: Record<
  string,
  { icon: PaymentIcon; className?: string }
> = {
  cashCoin: { icon: BsCashCoin, className: "text-emerald-400 h-6 w-6" },
  visa: { icon: VisaIcon, className: "h-10 w-10" },
  amex: { icon: AmexIcon, className: "h-10 w-10" },
  discover: { icon: DiscoverIcon, className: "h-10 w-10" },
  mastercard: { icon: MastercardIcon, className: "h-10 w-10" },
  apple_pay: { icon: ApplePayIcon, className: "h-10 w-10" },
  google_pay: { icon: GooglePayIcon, className: "h-10 w-10" },
  samsung_pay: { icon: SiSamsungpay, className: "text-blue-500 h-10 w-10" },
  card: {
    icon: IoCardOutline,
    className: "text-[rgb(var(--color-muted))] h-10 w-10",
  },
};

// TODO: enable other providers when supported by backend
export const TELEMETRY_PROVIDERS = [
  "nayax",
  "cpi",
  "cantaloupe",
  // "KioSoft",
  // "Parlevel",
];

// Maps telemetry provider to the expected API key header/name
export const TELEMETRY_API_KEY_NAMES: Record<string, string> = {
  nayax: "Lynx Api Key",
  cpi: "Optional Transport Secret",
  cantaloupe: "Optional Transport Secret",
};

export const ROUTE_OPTIMIZE_OPTIONS = [
  { value: "fastest", label: "Fastest", icon: FaShippingFast },
  {
    value: "shortest",
    label: "Shortest",
    icon: MdOutlineSwitchAccessShortcutAdd,
  },
];

export const PRIMARY_CONTACT_HELPER =
  "Main contact point between you and the location.";

export const SEND_REPORTS_HELPER =
  "Send commission, revenue, service history, and schedule reports to this contact's email.";

export const PORTAL_ACCESS_HELPER =
  "Creates a Vendistri account for the contact with view access to this location's commission, service schedule, activity, and history.";

export const TASK_TYPE_ICONS: Record<string, IconType> = {
  location_lead: FaBullseye,
  machine_repair: FaTools,
  machine_clean: MdCleanHands,
  machine_refill: FaArrowUpWideShort,
  machine_refund: MdOutlineMoneyOff,
  machine_collection: BsCashCoin,
  machine_install: IoMdAdd,
  machine_remove: IoMdRemoveCircleOutline,
  machine_service: FaServicestack,
  machine_pickup_inventory: FaWarehouse,
};

export const TASK_TYPE_ORDER: Record<string, number> = {
  machine_service: 0,
  machine_collection: 1,
  machine_refill: 2,
  machine_clean: 3,
  machine_pickup_inventory: 4,
  machine_repair: 5,
  machine_refund: 6,
  machine_install: 7,
  machine_remove: 8,
  location_lead: 9,
  other: 10,
  default: 11,
};

export const TASK_NOTES_PLACEHOLDER_BY_TYPE: Record<string, string> = {
  machine_repair:
    "Example: Bills not accepted; coin mech jammed; screen shows “Error 12”.",
  machine_clean: "Example: Wipe exterior, clean keypad/display, empty drip tray.",
  machine_refill: "Example: Low stock — refill chips + soda (A1, B2).",
  machine_refund: "Example: Refund request for jammed item on row B3.",
  machine_install: "Example: Install machine, connect power, test vend + card reader.",
  machine_remove: "Example: Remove machine, note any damage, confirm pickup time.",
  machine_collection: "Example: Collect cash, record totals, note discrepancies.",
  other: "Example: Describe the issue and what needs to be done.",
  machine_service: "Example: Service visit details (revenue, refunds, notes).",
  default: "Example: Describe the task and any requirements.",
};
